import * as React from 'react';
import { getBaseUrl } from '../../envUtils';
import { formatBearerToken } from './auth_utils';

/**
 * CompliancePanel
 * Phase 1 – Review/edit rules fetched from SceneDB, then Submit.
 * Phase 2 – Show PASS/FAIL per rule + highlight failed entities.
 */
const HIDDEN_ATTRS = new Set(['applicable_layer', 'applicable_layer_ids', 'Rule ID']);

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function fetchLayer(sceneId, stageId, layerId, accessToken) {
    const url = `${getBaseUrl()}/api/v2/scene/${sceneId}/stage/${stageId}/layer/${layerId}?layer_depth=1`;
    console.log('[CompliancePanel] fetchLayer →', url);
    const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': formatBearerToken(accessToken)
        }
    });
    if (!res.ok) throw new Error(`Layer ${layerId} fetch failed: HTTP ${res.status}`);
    return res.json();
}

async function modifyAttribute(payload, accessToken) {
    const res = await fetch(`${getBaseUrl()}/scene/modify_prim_attribute`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'accept': 'application/json',
            'Authorization': formatBearerToken(accessToken),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });
    if (!res.ok) {
        const txt = await res.text();
        console.warn('[CompliancePanel] modify_prim_attribute error:', txt);
    }
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ─── RuleEditRow ──────────────────────────────────────────────────────────────

function RuleEditRow({ rule, sceneId, stageId, layerId, subLayerId, accessToken }) {
    const contextPrim = rule.prims?.find(p => p.type === 'ContextPrim');
    const attrs = contextPrim
        ? (contextPrim.attributes || []).filter(a => !HIDDEN_ATTRS.has(a.name))
        : [];

    const [localValues, setLocalValues] = React.useState(() => {
        const init = {};
        attrs.forEach(a => { init[a.id] = a.value; });
        return init;
    });
    const [saving, setSaving] = React.useState({});

    if (!contextPrim) return null;

    const ruleDescAttr = attrs.find(a => a.name === 'rule_description');
    const applicableAttr = attrs.find(a => a.name === 'applicable');
    const otherAttrs = attrs.filter(a => a.name !== 'rule_description' && a.name !== 'applicable');

    const handleChange = async (attr, newValue) => {
        setLocalValues(prev => ({ ...prev, [attr.id]: newValue }));
        setSaving(prev => ({ ...prev, [attr.id]: true }));
        try {
            await modifyAttribute({
                scene_id: sceneId, stage_id: stageId,
                layer_id: layerId, sub_layer_id: subLayerId,
                prim_id: contextPrim.id,
                attribute_id: attr.id, attribute_value: String(newValue)
            }, accessToken);
        } finally {
            setSaving(prev => ({ ...prev, [attr.id]: false }));
        }
    };

    const isApplicable = localValues[applicableAttr?.id] === '1.0'
        || localValues[applicableAttr?.id] === '1'
        || Number(localValues[applicableAttr?.id]) === 1;

    return (
        <div style={S.ruleCard}>
            {ruleDescAttr && (
                <div style={S.ruleTitle}>
                    <span style={S.ruleBullet}>📋</span>
                    <span>{ruleDescAttr.value}</span>
                </div>
            )}
            {applicableAttr && (
                <label style={S.toggleRow}>
                    <div
                        role="button"
                        tabIndex={0}
                        style={{
                            ...S.toggleTrack,
                            background: isApplicable
                                ? 'linear-gradient(90deg,#f36f4b,#e05535)'
                                : 'rgba(255,255,255,0.1)'
                        }}
                        onClick={() => handleChange(applicableAttr, isApplicable ? '0.0' : '1.0')}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleChange(applicableAttr, isApplicable ? '0.0' : '1.0'); }}
                    >
                        <div style={{ ...S.toggleThumb, transform: isApplicable ? 'translateX(16px)' : 'translateX(2px)' }} />
                    </div>
                    <span style={{ color: isApplicable ? '#f36f4b' : '#888', fontSize: 12, marginLeft: 8 }}>
                        {isApplicable ? 'Applicable' : 'Not Applicable'}
                    </span>
                    {saving[applicableAttr.id] && <span style={S.savingDot}>●</span>}
                </label>
            )}
            {otherAttrs.length > 0 && (
                <div style={S.attrGrid}>
                    {otherAttrs.map(attr => (
                        <div key={attr.id} style={S.attrRow}>
                            <span style={S.attrLabel}>{attr.name.replace(/_/g, ' ')}</span>
                            <div style={S.inputWrap}>
                                <input
                                    style={S.attrInput}
                                    value={localValues[attr.id] ?? attr.value}
                                    onChange={e => setLocalValues(prev => ({ ...prev, [attr.id]: e.target.value }))}
                                    onBlur={e => handleChange(attr, e.target.value)}
                                />
                                {saving[attr.id] && <span style={S.savingDot}>●</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── RuleResultRow ────────────────────────────────────────────────────────────

function RuleResultRow({ rule, effectPrim }) {
    const contextPrim = rule.prims?.find(p => p.type === 'ContextPrim');
    const descAttr = contextPrim?.attributes?.find(a => a.name === 'rule_description');
    const [highlighted, setHighlighted] = React.useState(false);

    if (!effectPrim) {
        return (
            <div style={{ ...S.ruleCard, borderLeft: '3px solid #555' }}>
                {descAttr && <div style={S.ruleTitle}><span style={S.ruleBullet}>📋</span><span>{descAttr.value}</span></div>}
                <div style={{ color: '#666', fontSize: 12, marginTop: 6 }}>⏳ Result pending…</div>
            </div>
        );
    }

    const attrMap = {};
    (effectPrim.attributes || []).forEach(a => { attrMap[a.name] = a.value; });
    const status = attrMap['status'] || '';
    const description = attrMap['description'] || '';
    const failedEntities = attrMap['failed_entities'] || '';
    const isPassed = status.toUpperCase().includes('PASS');

    const doHighlight = () => {
        const ids = failedEntities.split(',').map(s => s.trim()).filter(Boolean);
        if (!window.highlightCesiumGlbEntities || ids.length === 0) return;
        if (highlighted) {
            if (typeof window._clearCesiumGlbHighlight === 'function') window._clearCesiumGlbHighlight();
            setHighlighted(false);
        } else {
            window.highlightCesiumGlbEntities(ids, {}, { data_characteristics: ['compliance_fail'] });
            setHighlighted(true);
        }
    };

    return (
        <div style={{
            ...S.ruleCard,
            borderLeft: `3px solid ${isPassed ? '#4caf50' : '#f44336'}`,
            background: isPassed ? 'rgba(76,175,80,0.06)' : 'rgba(244,67,54,0.06)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{
                    ...S.statusBadge,
                    background: isPassed
                        ? 'linear-gradient(135deg,#2e7d32,#388e3c)'
                        : 'linear-gradient(135deg,#b71c1c,#c62828)',
                }}>
                    {isPassed ? '✅ PASS' : '❌ FAIL'}
                </div>
                {!isPassed && failedEntities && (
                    <button
                        style={{ ...S.highlightBtn, background: highlighted ? 'rgba(244,67,54,0.3)' : 'rgba(244,67,54,0.1)' }}
                        onClick={doHighlight}
                    >
                        {highlighted ? '🔴 Clear' : '🔍 Highlight Failed'}
                    </button>
                )}
            </div>
            {descAttr && <div style={S.ruleTitle}><span style={S.ruleBullet}>📋</span><span>{descAttr.value}</span></div>}
            {description && (
                <div style={{ color: '#bbb', fontSize: 12, lineHeight: 1.6, marginTop: 6, padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 4 }}>
                    {description}
                </div>
            )}
        </div>
    );
}

// ─── DomainSection ────────────────────────────────────────────────────────────

function DomainSection({ domainName, rules, sceneId, stageId, layerId, accessToken, phase, effectPrimsMap }) {
    const [expanded, setExpanded] = React.useState(true);

    let passCount = 0, failCount = 0;
    if (phase === 'results') {
        rules.forEach(r => {
            const ep = effectPrimsMap?.[r.id];
            if (!ep) return;
            const st = (ep.attributes || []).find(a => a.name === 'status')?.value || '';
            if (st.toUpperCase().includes('PASS')) passCount++;
            else failCount++;
        });
    }

    const cleanName = domainName.replace(/_rules$/i, '').replace(/_/g, ' ');

    return (
        <div style={S.domainSection}>
            <button style={S.domainHeader} onClick={() => setExpanded(e => !e)}>
                <span style={{ fontSize: 13 }}>{expanded ? '▼' : '▶'}</span>
                <span style={{ marginLeft: 8, flex: 1, textAlign: 'left' }}>{cleanName}</span>
                {phase === 'results' ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                        {passCount > 0 && <span style={S.passTag}>{passCount} ✅</span>}
                        {failCount > 0 && <span style={S.failTag}>{failCount} ❌</span>}
                    </div>
                ) : (
                    <span style={S.ruleCount}>{rules.length} rules</span>
                )}
            </button>
            {expanded && (
                <div style={S.domainBody}>
                    {rules.map(rule => (
                        phase === 'results' ? (
                            <RuleResultRow key={rule.id} rule={rule} effectPrim={effectPrimsMap?.[rule.id]} />
                        ) : (
                            <RuleEditRow key={rule.id} rule={rule} sceneId={sceneId} stageId={stageId}
                                layerId={layerId} subLayerId={rule.id} accessToken={accessToken} />
                        )
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── StatusOverlay ────────────────────────────────────────────────────────────

function StatusOverlay({ message }) {
    return (
        <div style={S.overlay}>
            <div style={S.overlayBox}>
                <div style={S.spinnerRing} />
                <div style={{ color: '#f36f4b', fontWeight: 600, fontSize: 14, marginTop: 16, textAlign: 'center', maxWidth: 220 }}>
                    {message}
                </div>
            </div>
        </div>
    );
}

// ─── Main CompliancePanel ─────────────────────────────────────────────────────

function CompliancePanel({ responseData, accessToken, pollJobStatus, promptId }) {
    const result = responseData.result || {};
    const sublayerIdsMap = result.sublayer_ids_map || {};
    const complianceSubtask = responseData.compliance_subtask;
    const sceneId = result.scene_id;
    const stageId = (result.stage_ids || [])[0];

    console.log('[CompliancePanel] mounted — sceneId:', sceneId, 'stageId:', stageId,
        'complianceSubtask:', complianceSubtask, 'promptId:', promptId,
        'domainLayers:', Object.keys(sublayerIdsMap));

    const [phase, setPhase] = React.useState('edit');
    const [domains, setDomains] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [effectPrimsMap, setEffectPrimsMap] = React.useState({});
    const [submitError, setSubmitError] = React.useState(null);
    const [statusMsg, setStatusMsg] = React.useState('');

    // ── Phase 1: load domain layers ───────────────────────────────────────────
    React.useEffect(() => {
        let cancelled = false;
        async function load() {
            console.log('[CompliancePanel] loading domains, domainLayerIds:', Object.keys(sublayerIdsMap));
            try {
                const domainLayerIds = Object.keys(sublayerIdsMap).map(Number);
                const domainData = await Promise.all(
                    domainLayerIds.map(async (layerId) => {
                        const layer = await fetchLayer(sceneId, stageId, layerId, accessToken);
                        console.log('[CompliancePanel] domain', layerId, '→', layer.name, 'sub_layers:', layer.sub_layers?.length);
                        return { layerId, name: layer.name || `Domain ${layerId}`, rules: layer.sub_layers || [] };
                    })
                );
                if (!cancelled) {
                    setDomains(domainData);
                    setLoading(false);
                    console.log('[CompliancePanel] domains ready:', domainData.map(d => d.name));
                }
            } catch (err) {
                console.error('[CompliancePanel] load error:', err);
                if (!cancelled) { setError(err.message); setLoading(false); }
            }
        }
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        console.log('[CompliancePanel] handleSubmit — subtask:', complianceSubtask, 'promptId:', promptId);
        setPhase('submitting');
        setSubmitError(null);
        try {
            // 1. Signal backend that rules review is done
            setStatusMsg('Submitting compliance rules…');
            const statusUrl = `${getBaseUrl()}/arche-engine/set_status/${complianceSubtask}`;
            console.log('[CompliancePanel] POST', statusUrl);
            const res = await fetch(statusUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'accept': 'application/json',
                    'Authorization': formatBearerToken(accessToken),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: 'done' })
            });
            console.log('[CompliancePanel] set_status →', res.status);
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(`set_status failed (${res.status}): ${txt}`);
            }

            // 2. Wait 2 s so backend moves job away from 'user_input_needed'
            setStatusMsg('Starting assessment engine…');
            console.log('[CompliancePanel] waiting 2 s before polling…');
            await sleep(2000);

            // 3. Poll until results
            setPhase('polling');
            setStatusMsg('Running compliance assessment — this may take a minute…');
            const pollUrl = `${getBaseUrl()}/arche-engine/status/${promptId}`;
            console.log('[CompliancePanel] polling', pollUrl);
            const rawResult = await pollJobStatus(pollUrl, {
                maxAttempts: 120, intervalMs: 3000,
                backoffMultiplier: 1.05, maxIntervalMs: 8000,
                accessToken
            });
            console.log('[CompliancePanel] poll done, keys:', Object.keys(rawResult || {}));

            // 4. Resolve scene/stage from new result
            setStatusMsg('Parsing compliance results…');
            const newInner = rawResult?.result || rawResult || {};
            const newSceneId = newInner.scene_id || sceneId;
            const newStageId = (newInner.stage_ids || [])[0] || stageId;
            const newSubMap  = newInner.sublayer_ids_map || {};
            const newDomainIds = Object.keys(newSubMap).map(Number);
            console.log('[CompliancePanel] result domain IDs:', newDomainIds);

            // 5. Fetch EffectPrims
            const effectMap = {};
            await Promise.all(
                newDomainIds.map(async (domainLayerId) => {
                    try {
                        const domainLayer = await fetchLayer(newSceneId, newStageId, domainLayerId, accessToken);
                        for (const subLayer of (domainLayer.sub_layers || [])) {
                            const ep = (subLayer.prims || []).find(p => p.type === 'EffectPrim');
                            console.log(`[CompliancePanel] subLayer ${subLayer.id} EffectPrim:`, ep ? 'found' : 'none');
                            if (ep) effectMap[subLayer.id] = ep;
                        }
                    } catch (e) {
                        console.warn('[CompliancePanel] domain fetch error', domainLayerId, e.message);
                    }
                })
            );

            console.log('[CompliancePanel] effectMap entries:', Object.keys(effectMap).length);
            setEffectPrimsMap(effectMap);
            setPhase('results');
            setStatusMsg('');
            if (typeof window._clearCesiumGlbHighlight === 'function') window._clearCesiumGlbHighlight();

        } catch (err) {
            console.error('[CompliancePanel] submit error:', err);
            setSubmitError(err.message);
            setPhase('edit');
            setStatusMsg('');
        }
    };

    // ── Stats ─────────────────────────────────────────────────────────────────
    const totalRules = domains.reduce((s, d) => s + d.rules.length, 0);
    const passCount  = Object.values(effectPrimsMap).filter(ep =>
        (ep.attributes || []).find(a => a.name === 'status')?.value?.toUpperCase().includes('PASS')
    ).length;
    const failCount  = Object.values(effectPrimsMap).filter(ep =>
        !(ep.attributes || []).find(a => a.name === 'status')?.value?.toUpperCase().includes('PASS')
    ).length;

    const isProcessing = phase === 'submitting' || phase === 'polling';

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) return (
        <div style={S.container}>
            <div style={S.heroHeader}>
                <span style={S.heroIcon}>⚖️</span>
                <div><div style={S.heroTitle}>Compliance Assessment</div><div style={S.heroSub}>Loading compliance rules…</div></div>
            </div>
            <div style={S.loadingDots}>
                <span style={{ ...S.dot, animationDelay: '0s' }} />
                <span style={{ ...S.dot, animationDelay: '.15s' }} />
                <span style={{ ...S.dot, animationDelay: '.3s' }} />
            </div>
        </div>
    );

    // ── Error ─────────────────────────────────────────────────────────────────
    if (error) return (
        <div style={S.container}>
            <div style={S.heroHeader}>
                <span style={S.heroIcon}>⚖️</span>
                <div style={S.heroTitle}>Compliance Assessment</div>
            </div>
            <div style={S.errorBox}>⚠️ {error}</div>
        </div>
    );

    // ── Processing ────────────────────────────────────────────────────────────
    if (isProcessing) return (
        <div style={{ ...S.container, position: 'relative', minHeight: 300 }}>
            <div style={S.heroHeader}>
                <span style={S.heroIcon}>⚖️</span>
                <div><div style={S.heroTitle}>Compliance Assessment</div><div style={S.heroSub}>{statusMsg}</div></div>
            </div>
            <StatusOverlay message={statusMsg} />
        </div>
    );

    // ── Results ───────────────────────────────────────────────────────────────
    if (phase === 'results') return (
        <div style={S.container}>
            <div style={S.heroHeader}>
                <span style={S.heroIcon}>⚖️</span>
                <div><div style={S.heroTitle}>Compliance Results</div><div style={S.heroSub}>Assessment complete</div></div>
            </div>
            <div style={S.summaryBar}>
                <div style={S.summaryItem}><span style={{ fontSize: 22 }}>📊</span><span style={S.summaryLabel}>Total</span><span style={S.summaryVal}>{totalRules}</span></div>
                <div style={S.divider} />
                <div style={S.summaryItem}><span style={{ fontSize: 22 }}>✅</span><span style={S.summaryLabel}>Pass</span><span style={{ ...S.summaryVal, color: '#4caf50' }}>{passCount}</span></div>
                <div style={S.divider} />
                <div style={S.summaryItem}><span style={{ fontSize: 22 }}>❌</span><span style={S.summaryLabel}>Fail</span><span style={{ ...S.summaryVal, color: '#f44336' }}>{failCount}</span></div>
            </div>
            <div style={S.domainList}>
                {domains.map(domain => (
                    <DomainSection key={domain.layerId} domainName={domain.name} rules={domain.rules}
                        sceneId={sceneId} stageId={stageId} layerId={domain.layerId}
                        accessToken={accessToken} phase="results" effectPrimsMap={effectPrimsMap} />
                ))}
            </div>
        </div>
    );

    // ── Edit (Phase 1) ────────────────────────────────────────────────────────
    return (
        <div style={S.container}>
            <div style={S.heroHeader}>
                <span style={S.heroIcon}>⚖️</span>
                <div><div style={S.heroTitle}>Compliance Assessment</div><div style={S.heroSub}>Review rules · adjust parameters · submit</div></div>
            </div>
            <div style={S.infoBox}>
                ℹ️ Review the compliance rules below. Toggle applicability or adjust parameters,
                then click <strong>Submit</strong> to run the assessment.
            </div>
            {submitError && <div style={S.errorBox}>⚠️ {submitError}</div>}
            <div style={S.domainList}>
                {domains.map(domain => (
                    <DomainSection key={domain.layerId} domainName={domain.name} rules={domain.rules}
                        sceneId={sceneId} stageId={stageId} layerId={domain.layerId}
                        accessToken={accessToken} phase="edit" effectPrimsMap={{}} />
                ))}
            </div>
            <div style={S.submitWrapper}>
                <button style={S.submitBtn} onMouseDown={handleSubmit}>
                    <span style={{ fontSize: 16 }}>🚀</span>
                    <span>Submit Compliance Review</span>
                    <span style={{ fontSize: 12, opacity: 0.75 }}>({totalRules} rules)</span>
                </button>
            </div>
        </div>
    );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
    container: { padding: '0 0 100px', color: '#e0e0e0', fontFamily: '"Suisse Intl","Helvetica Neue",sans-serif', fontSize: 13 },
    heroHeader: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 12px', background: 'linear-gradient(135deg,rgba(243,111,75,0.15),rgba(224,85,53,0.08))', borderBottom: '1px solid rgba(243,111,75,0.2)', marginBottom: 12 },
    heroIcon: { fontSize: 32, lineHeight: 1 },
    heroTitle: { fontSize: 16, fontWeight: 700, color: '#f36f4b', letterSpacing: 0.3 },
    heroSub: { fontSize: 11, color: '#aaa', marginTop: 2 },
    infoBox: { margin: '0 12px 12px', padding: '10px 14px', background: 'rgba(243,111,75,0.08)', border: '1px solid rgba(243,111,75,0.2)', borderRadius: 8, color: '#ccc', fontSize: 12, lineHeight: 1.6 },
    errorBox: { margin: '0 12px 12px', padding: '10px 14px', background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 8, color: '#f44336', fontSize: 12 },
    summaryBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-around', margin: '0 12px 14px', padding: 14, background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)' },
    summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 },
    summaryLabel: { color: '#aaa', fontSize: 11 },
    summaryVal: { color: '#fff', fontWeight: 700, fontSize: 18 },
    divider: { width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.1)' },
    domainList: { display: 'flex', flexDirection: 'column', gap: 8, padding: '0 12px' },
    domainSection: { background: 'rgba(255,255,255,0.03)', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' },
    domainHeader: { width: '100%', background: 'linear-gradient(90deg,rgba(243,111,75,0.15),rgba(243,111,75,0.05))', border: 'none', color: '#f36f4b', fontWeight: 600, fontSize: 13, padding: '11px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 },
    ruleCount: { fontSize: 11, color: '#888', fontWeight: 400, background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 10 },
    passTag: { fontSize: 11, color: '#4caf50', background: 'rgba(76,175,80,0.15)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 },
    failTag: { fontSize: 11, color: '#f44336', background: 'rgba(244,67,54,0.15)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 },
    domainBody: { padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 8 },
    ruleCard: { background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '12px 14px', borderLeft: '3px solid rgba(243,111,75,0.35)' },
    ruleTitle: { display: 'flex', gap: 8, color: '#ddd', fontSize: 12, lineHeight: 1.55, marginBottom: 8 },
    ruleBullet: { flexShrink: 0, fontSize: 13 },
    toggleRow: { display: 'flex', alignItems: 'center', marginBottom: 8, cursor: 'pointer' },
    toggleTrack: { width: 36, height: 20, borderRadius: 10, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 },
    toggleThumb: { position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'transform 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' },
    attrGrid: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 },
    attrRow: { display: 'flex', alignItems: 'center', gap: 10 },
    attrLabel: { color: '#888', fontSize: 11, minWidth: 130, textTransform: 'capitalize' },
    inputWrap: { flex: 1, display: 'flex', alignItems: 'center', gap: 6 },
    attrInput: { flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 5, padding: '5px 9px', color: '#e0e0e0', fontSize: 12, outline: 'none' },
    savingDot: { color: '#f36f4b', fontSize: 9, flexShrink: 0 },
    statusBadge: { padding: '4px 12px', borderRadius: 20, color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, flexShrink: 0 },
    highlightBtn: { padding: '4px 12px', color: '#f44336', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 },
    submitWrapper: { position: 'sticky', bottom: 0, padding: '14px 12px 20px', background: 'linear-gradient(to top,rgba(20,20,30,1) 60%,transparent)' },
    submitBtn: { width: '100%', padding: 13, background: 'linear-gradient(90deg,#f36f4b,#e05535)', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: '0 4px 20px rgba(243,111,75,0.45)' },
    overlay: { position: 'absolute', inset: 0, background: 'rgba(15,15,25,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 },
    overlayBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
    spinnerRing: { width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(243,111,75,0.2)', borderTopColor: '#f36f4b', animation: 'tw-spin 0.9s linear infinite' },
    loadingDots: { display: 'flex', justifyContent: 'center', gap: 8, padding: '40px 0' },
    dot: { width: 10, height: 10, borderRadius: '50%', background: '#f36f4b', display: 'inline-block', animation: 'tw-bounce 0.9s ease-in-out infinite' },
};

export default CompliancePanel;
